import React from "react";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

interface ConfirmActionDialogProps {
  open: boolean;
  title: string;
  /** What will happen, spelled out — shown as a warning. */
  message: React.ReactNode;
  /** Names the action, e.g. "Remove member" — never a bare "OK". */
  confirmLabel: string;
  /**
   * When set, the user must type this text to enable the confirm button.
   * Reserve for high-impact actions (deactivate partner, bulk operations).
   */
  confirmText?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Shared gate for destructive actions. The gate lives BEFORE the action on
 * purpose: offboarding emails and entitlement revocations can't be undone,
 * so there is no optimistic "undo" path.
 */
export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  open,
  title,
  message,
  confirmLabel,
  confirmText,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [typed, setTyped] = React.useState("");

  React.useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const typeMatches =
    !confirmText || typed.trim().toLowerCase() === confirmText.toLowerCase();

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: confirmText ? 2 : 0 }}>
          {message}
        </Alert>
        {confirmText && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              To confirm, type <strong>{confirmText}</strong> below:
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={confirmText}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              disabled={loading}
              error={typed.length > 0 && !typeMatches}
              helperText={
                typed.length > 0 && !typeMatches
                  ? `Please type "${confirmText}" exactly`
                  : ""
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading} autoFocus={!confirmText}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={loading || !typeMatches}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
